import { ArrowLeft, ArrowUp, BookmarkSimple, Plus, X } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useDocument } from "~/api/queries";
import { CommandProposalCard } from "~/features/command/CommandProposalCard";
import { proposeCommand } from "~/features/command/commandApi";
import { screenContextFor } from "~/features/command/commandModel";
import { Prose } from "~/features/work/Prose";
import { useThinkerDock } from "~/features/work/piles/pilesApi";
import { WorkspaceDecisions } from "~/features/work/WorkspaceDecisions";
import { ActionRequired } from "~/foundation/components/ActionRequired";
import { Button } from "~/foundation/components/Button";
import { cn } from "~/foundation/utils/cn";
import {
  type ConversationTurn,
  useConversationLibrary,
  useConversationMutation,
  useConversationScope,
  useWorkspaceComposer,
  useWorkspaceConversation,
  useWorkspaceWorkers,
  type WorkspaceConversation,
} from "./conversationApi";

type Tab = "decisions" | "conversation" | "history" | "actions" | "workers";

export function WorkspaceThinkerDock() {
  const scope = useConversationScope();
  return scope ? <Dock key={scope} scope={scope} /> : null;
}

function Dock({ scope }: { scope: string }) {
  const { t } = useTranslation();
  const { open, prefill, close, section, sectionRequest } = useThinkerDock();
  const { drafts, setDraft, submission, consumed } = useWorkspaceComposer();
  const storageKey = `workspace-conversation:${scope}`;
  const [id, setId] = useState<string | null>(() => sessionStorage.getItem(storageKey));
  const [tab, setTab] = useState<Tab>(section);
  // biome-ignore lint/correctness/useExhaustiveDependencies: repeated requests must reopen the chosen tab even when its name is unchanged.
  useEffect(() => {
    if (open) setTab(section);
  }, [open, section, sectionRequest]);
  const [pendingPrompt, setPendingPrompt] = useState("");
  const [localError, setLocalError] = useState("");
  const [archive, setArchive] = useState(false);
  const [worker, setWorker] = useState("");
  const [includePage, setIncludePage] = useState(true);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const pageContext = screenContextFor(pathname);
  const page = useDocument(open && includePage ? (pageContext.documentId ?? "") : "");
  const conversation = useWorkspaceConversation(id);
  const library = useConversationLibrary(open);
  const workers = useWorkspaceWorkers(open);
  const mutate = useConversationMutation();
  const scroll = useRef<HTMLDivElement>(null);
  const row = conversation.data;
  const draft = drafts[scope] ?? "";
  const waiting =
    mutate.isPending ||
    !!row?.turns.some(
      (turn) => turn.status === "thinking" && Date.now() - new Date(turn.at).getTime() < 300000,
    );
  const select = (next: string | null) => {
    setId(next);
    setTab("conversation");
    setLocalError("");
    mutate.reset();
  };
  useEffect(() => {
    if (id) sessionStorage.setItem(storageKey, id);
    else sessionStorage.removeItem(storageKey);
  }, [id, storageKey]);
  useEffect(() => {
    if (open && prefill) {
      setDraft(scope, prefill);
      setTab("conversation");
    }
  }, [open, prefill, scope, setDraft]);
  useEffect(() => {
    if (tab === "conversation" && scroll.current && (row?.version || pendingPrompt))
      scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [row?.version, pendingPrompt, tab]);
  useEffect(() => {
    if (tab !== "conversation" && scroll.current) scroll.current.scrollTop = 0;
  }, [tab]);
  useEffect(() => {
    if (!open) return;
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", dismissOnEscape);
    return () => window.removeEventListener("keydown", dismissOnEscape);
  }, [open, close]);

  const send = useCallback(
    (text: string, requestId: string = crypto.randomUUID()) => {
      if (!text.trim()) return;
      if (waiting) {
        setLocalError(t("Wait for the current answer before sending another prompt."));
        return;
      }
      if (id && !row) {
        setLocalError(t("Load the conversation before continuing, or start a new one."));
        return;
      }
      if (row?.archivedAt) {
        setLocalError(t("Restore this conversation before continuing."));
        return;
      }
      if (row && !row.sourcesAvailable) {
        setLocalError(t("Some sources are no longer available. Start a new conversation."));
        return;
      }
      const nextId = id ?? crypto.randomUUID();
      const context = screenContextFor(pathname);
      const candidateId = page.data?.id ?? context.documentId;
      const documentId =
        includePage && candidateId && /^[\da-f]{8}-[\da-f-]{27}$/i.test(candidateId)
          ? candidateId
          : undefined;
      setPendingPrompt(text);
      setLocalError("");
      setTab("conversation");
      mutate.mutate(
        {
          method: "send",
          id: nextId,
          turnId: requestId,
          version: row?.version ?? 0,
          prompt: text,
          route: includePage ? pathname : "/",
          ...(documentId ? { documentId } : {}),
          ...(worker ? { workerId: worker } : {}),
        },
        {
          onSuccess: () => {
            setId(nextId);
            if (useWorkspaceComposer.getState().drafts[scope]?.trim() === text.trim())
              setDraft(scope, "");
          },
          onSettled: () => setPendingPrompt(""),
        },
      );
    },
    [waiting, t, id, row, pathname, includePage, mutate, worker, scope, setDraft, page.data?.id],
  );
  useEffect(() => {
    if (submission?.scope !== scope || (id && conversation.isLoading)) return;
    consumed();
    send(submission.prompt, submission.id);
  }, [submission, scope, id, conversation.isLoading, consumed, send]);

  const action = async (method: string, extra: Record<string, unknown> = {}) => {
    if (!row) return;
    setLocalError("");
    await mutate.mutateAsync({ method, id: row.id, version: row.version, ...extra });
  };
  const update = (kind: string, turnId?: string) => {
    void action("update", { action: kind, ...(turnId ? { turnId } : {}) }).catch(() => {});
  };
  const error =
    localError || mutate.error?.message || conversation.error?.message || library.error?.message;
  const tabLabels: Record<Tab, string> = {
    conversation: t("Conversation"),
    decisions: t("Decisions"),
    history: t("History"),
    actions: t("Saved actions"),
    workers: t("Thinkers"),
  };

  return (
    <aside
      id="workspace-thinker-panel"
      aria-label={t("Workspace thinker")}
      // The class hides it visually; the attribute takes the closed dock out of the accessibility
      // tree too, so its Send button and tabs never shadow the page's own while it is shut.
      hidden={!open}
      className={cn(
        "min-h-0 min-w-0 flex-1 flex-col border-divider bg-bg text-text lg:w-[400px] lg:max-w-[42vw] lg:flex-none lg:border-s xl:w-[440px]",
        open ? "flex" : "hidden",
      )}
    >
      <header className="flex shrink-0 items-start justify-between gap-2 border-b border-divider px-4 py-3">
        <div>
          <h2 className="text-body font-semibold">{t("Workspace thinker")}</h2>
          <p className="mt-0.5 text-caption text-text-secondary">
            {t("You direct. Your thinker coordinates.")}
          </p>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={t("New conversation")}
            disabled={mutate.isPending}
            onClick={() => {
              select(null);
              setDraft(scope, "");
            }}
            className="rounded p-1.5 hover:bg-hover-bg disabled:opacity-40"
          >
            <Plus size={18} aria-hidden />
          </button>
          <button
            type="button"
            aria-label={t("Back to page")}
            onClick={close}
            className="rounded p-1.5 hover:bg-hover-bg"
          >
            <span className="lg:hidden">
              <ArrowLeft size={18} aria-hidden />
            </span>
            <span className="hidden lg:inline">
              <X size={18} aria-hidden />
            </span>
          </button>
        </div>
      </header>
      <nav
        aria-label={t("Workspace thinker sections")}
        className="flex shrink-0 gap-1 border-b border-divider px-2"
      >
        {(Object.keys(tabLabels) as Tab[]).map((key) => (
          <button
            key={key}
            type="button"
            aria-current={tab === key ? "page" : undefined}
            aria-label={tabLabels[key]}
            onClick={() => setTab(key)}
            className={cn(
              "min-w-0 flex-1 border-b-2 px-1 py-2.5 text-caption",
              tab === key
                ? "border-accent font-medium text-accent"
                : "border-transparent text-text-secondary hover:text-text",
            )}
          >
            {key === "conversation" ? t("Chat") : tabLabels[key]}
          </button>
        ))}
      </nav>
      {error ? (
        <p
          role="alert"
          className="shrink-0 border-b border-divider px-4 py-2 text-small text-danger"
        >
          {error}
        </p>
      ) : null}
      <div ref={scroll} className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">
        {tab === "decisions" ? <WorkspaceDecisions /> : null}
        {tab === "conversation" ? (
          <>
            {row ? (
              <div className="mb-5 flex flex-wrap items-center gap-2 text-caption text-text-secondary">
                <span className="max-w-full truncate font-medium text-text">{row.title}</span>
                <span>
                  {row.archivedAt
                    ? t("Archived")
                    : row.kept
                      ? t("Kept conversation")
                      : t("Temporary")}
                </span>
                {!row.archivedAt && !row.kept ? (
                  <button
                    type="button"
                    disabled={waiting}
                    onClick={() => update("keep")}
                    className="text-accent underline"
                  >
                    {t("Keep conversation")}
                  </button>
                ) : null}
                <button
                  type="button"
                  disabled={waiting || row.turns.some((turn) => turn.status === "working")}
                  onClick={() => update(row.archivedAt ? "restore" : "archive")}
                  className="text-accent underline disabled:opacity-40"
                >
                  {row.archivedAt ? t("Restore conversation") : t("Delete to archive")}
                </button>
              </div>
            ) : null}
            {!row && !pendingPrompt ? (
              <div className="py-6">
                <h3 className="text-h3 font-semibold">{t("What would you like to work on?")}</h3>
                <p className="mt-2 text-small leading-relaxed text-text-secondary">
                  {t(
                    "Discuss an idea, give an instruction, or gather documents on your Bench. Follow up here from any page.",
                  )}
                </p>
                <div className="mt-5 flex flex-col gap-2">
                  {[
                    t("Gather business-model documents into a pile on my Bench."),
                    t("Help me decide what to focus on next."),
                    t("Which thinkers can help me with this workspace?"),
                  ].map((text) => (
                    <button
                      type="button"
                      key={text}
                      onClick={() => setDraft(scope, text)}
                      className="rounded-md border border-divider p-3 text-start text-small hover:bg-hover-bg"
                    >
                      {text}
                    </button>
                  ))}
                </div>
                <p className="mt-5 text-caption text-text-secondary">
                  {t(
                    "Temporary conversations stay in your history until you archive them. Keep the ones you want to return to.",
                  )}
                </p>
              </div>
            ) : null}
            {row && !row.sourcesAvailable ? (
              <p role="status" className="mb-4 text-small text-text-secondary">
                {t("Some sources are no longer available. Start a new conversation.")}
              </p>
            ) : null}
            <div className="space-y-6" aria-live="polite" aria-relevant="additions text">
              {row?.turns.map((turn) => (
                <Turn
                  key={turn.id}
                  turn={turn}
                  row={row}
                  busy={waiting}
                  action={action}
                  update={update}
                  reuse={(text) => setDraft(scope, text)}
                  retry={() => send(turn.prompt, turn.id)}
                />
              ))}
              {pendingPrompt ? (
                <div className="space-y-3">
                  <p className="rounded-md bg-sidebar-bg p-3 text-small whitespace-pre-wrap">
                    {pendingPrompt}
                  </p>
                  <p role="status" className="text-small text-text-secondary">
                    {t("Thinking… You can return to your page.")}
                  </p>
                </div>
              ) : null}
            </div>
            {row?.audit.length ? (
              <details className="mt-6 text-caption text-text-secondary">
                <summary className="cursor-pointer">{t("Conversation audit")}</summary>
                <ul className="mt-2 space-y-1">
                  {row.audit.map((event) => (
                    <li key={`${event.at}-${event.action}`}>
                      {new Date(event.at).toLocaleString()} · {auditLabel(event.action, t)}
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </>
        ) : null}
        {tab === "history" ? (
          <div className="space-y-6">
            <section>
              <h3 className="mb-2 text-small font-semibold">{t("Last 10 prompts")}</h3>
              {!library.data?.prompts.length ? (
                <p className="text-small text-text-secondary">
                  {t("Your prompts will appear here.")}
                </p>
              ) : null}
              <ol className="space-y-1">
                {library.data?.prompts.map((item) => (
                  <li key={item.id} className="flex gap-2 rounded border border-divider p-2">
                    <button
                      type="button"
                      onClick={() => select(item.conversationId)}
                      className="min-w-0 flex-1 text-start text-small"
                    >
                      <span className="line-clamp-2">{item.prompt}</span>
                      <span className="text-caption text-text-secondary">
                        {new Date(item.at).toLocaleString()}
                        {item.archivedAt ? ` · ${t("Archived")}` : ""}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDraft(scope, item.prompt);
                        select(null);
                      }}
                      className="self-start text-caption text-accent"
                    >
                      {t("Reuse")}
                    </button>
                  </li>
                ))}
              </ol>
            </section>
            <section>
              <div className="mb-2 flex justify-between gap-2">
                <h3 className="text-small font-semibold">
                  {archive ? t("Audited archive") : t("Conversations")}
                </h3>
                <button
                  type="button"
                  onClick={() => setArchive(!archive)}
                  className="text-caption text-accent"
                >
                  {archive ? t("Show active") : t("Show archive")}
                </button>
              </div>
              <p className="mb-2 text-caption text-text-secondary">
                {archive
                  ? t(
                      "Deleted conversations are retained here with their audit trail. Restore one to continue.",
                    )
                  : t("Recent conversations, including temporary ones.")}
              </p>
              {library.data?.conversations
                .filter((c) => !!c.archivedAt === archive)
                .map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => select(c.id)}
                    className="mb-1 block w-full rounded border border-divider p-2 text-start text-small hover:bg-hover-bg"
                  >
                    <span className="line-clamp-2">{c.title}</span>
                    <span className="text-caption text-text-secondary">
                      {c.kept ? t("Kept conversation") : t("Temporary")} ·{" "}
                      {new Date(c.updatedAt).toLocaleDateString()}
                    </span>
                  </button>
                ))}
            </section>
          </div>
        ) : null}
        {tab === "actions" ? (
          <section>
            <h3 className="text-small font-semibold">{t("Saved actions")}</h3>
            <p className="my-2 text-small text-text-secondary">
              {t(
                "Reusable instructions. Reusing one puts it in the composer for you to review and send.",
              )}
            </p>
            {!library.data?.savedActions.length ? (
              <p className="mt-5 text-small text-text-secondary">
                {t("Use Save action beneath any prompt to keep it here.")}
              </p>
            ) : null}
            {library.data?.savedActions.map((item) => (
              <div key={item.id} className="mt-3 rounded-md border border-divider p-3">
                <p className="text-small whitespace-pre-wrap">{item.prompt}</p>
                <div className="mt-3 flex gap-3 text-caption text-accent">
                  <button
                    type="button"
                    onClick={() => {
                      select(null);
                      setDraft(scope, item.prompt);
                    }}
                  >
                    {t("Reuse action")}
                  </button>
                  <button type="button" onClick={() => select(item.conversationId)}>
                    {t("Open conversation")}
                  </button>
                </div>
              </div>
            ))}
          </section>
        ) : null}
        {tab === "workers" ? (
          <section>
            <h3 className="text-small font-semibold">{t("Available to your workspace thinker")}</h3>
            <p className="my-2 text-small leading-relaxed text-text-secondary">
              {t(
                "Your thinker prepares a brief, delegates after your review, and brings the result back here. You can stop an IDE job while it runs.",
              )}
            </p>
            {workers.error ? (
              <p role="alert" className="text-small text-danger">
                {workers.error.message}
              </p>
            ) : null}
            {workers.isLoading ? <p role="status">{t("Loading…")}</p> : null}
            {workers.data?.map((w) => (
              <div key={w.id} className="mt-2 rounded border border-divider p-3">
                <div className="flex items-start justify-between gap-2">
                  <span className="text-small font-medium">{w.name}</span>
                  <span className="shrink-0 text-caption text-text-secondary">
                    {w.available
                      ? w.kind === "ide"
                        ? t("Online · IDE")
                        : t("AI thinker")
                      : t("Offline")}
                  </span>
                </div>
                {w.workspacePath ? (
                  <p className="mt-1 break-all text-caption text-text-secondary">
                    {w.workspacePath}
                  </p>
                ) : null}
                <button
                  type="button"
                  disabled={!w.available}
                  onClick={() => {
                    setWorker(w.id);
                    setTab("conversation");
                  }}
                  className="mt-2 text-caption text-accent disabled:opacity-40"
                >
                  {t("Prepare a brief")}
                </button>
              </div>
            ))}
            <Link
              to="/thinker"
              onClick={close}
              className="mt-5 block text-small text-accent underline"
            >
              {t("Thinker settings")}
            </Link>
          </section>
        ) : null}
      </div>
      {tab === "conversation" ? (
        <footer className="shrink-0 border-t border-divider bg-bg px-4 pb-[max(12px,env(safe-area-inset-bottom))] pt-3">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              send(draft.trim());
            }}
          >
            <label htmlFor="workspace-thinker-message" className="sr-only">
              {t("Message workspace thinker")}
            </label>
            <textarea
              id="workspace-thinker-message"
              value={draft}
              onChange={(event) => setDraft(scope, event.target.value)}
              maxLength={2000}
              onKeyDown={(event) => {
                if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
                  event.preventDefault();
                  send(draft.trim());
                }
              }}
              placeholder={t("Discuss, instruct, or follow up…")}
              className="max-h-40 min-h-20 w-full resize-y rounded-md border border-input-border bg-bg p-2.5 text-small focus:border-input-border-focus"
            />
            <div className="mt-2 flex items-center justify-between gap-2">
              <label className="flex items-center gap-1.5 text-caption text-text-secondary">
                <input
                  type="checkbox"
                  checked={includePage}
                  onChange={(e) => setIncludePage(e.target.checked)}
                />
                {t("Use current page")}
              </label>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!draft.trim() || waiting || !!row?.archivedAt}
              >
                <ArrowUp size={15} aria-hidden />
                {waiting ? t("Thinking…") : t("Send")}
              </Button>
            </div>
            {worker ? (
              <div className="mt-2 flex items-center justify-between gap-2 text-caption text-text-secondary">
                <span>
                  {t("Brief for {{name}}", {
                    name: workers.data?.find((w) => w.id === worker)?.name ?? worker,
                  })}
                </span>
                <button type="button" onClick={() => setWorker("")} className="text-accent">
                  {t("Clear")}
                </button>
              </div>
            ) : null}
          </form>
        </footer>
      ) : null}
    </aside>
  );
}

function Turn({
  turn,
  row,
  busy,
  action,
  update,
  reuse,
  retry,
}: {
  turn: ConversationTurn;
  row: WorkspaceConversation;
  busy: boolean;
  action: (method: string, extra?: Record<string, unknown>) => Promise<void>;
  update: (kind: string, turnId?: string) => void;
  reuse: (text: string) => void;
  retry: () => void;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const proposal = useMutation({
    mutationFn: () =>
      proposeCommand(api, turn.plan?.brief ?? turn.prompt, screenContextFor(turn.route ?? "/")),
  });
  const cancel = useMutation({
    mutationFn: async () => {
      if (turn.dispatchId) {
        await api.ide.dispatches.cancel({
          id: turn.dispatchId,
          reason: "Stopped by the person in workspace conversation",
        });
        await action("sync", { turnId: turn.id });
      }
    },
  });
  const [receiptError, setReceiptError] = useState("");
  const paragraphs = turn.answer?.split(/\n\s*\n/) ?? [];
  if (paragraphs.length > 1 && /^#{1,6}\s+[^\n]+$/.test(paragraphs[0] ?? "")) {
    paragraphs.splice(0, 2, paragraphs.slice(0, 2).join("\n\n"));
  }
  const inactive = busy || !!row.archivedAt || !row.sourcesAvailable;
  return (
    <article className="space-y-3">
      <div className="rounded-md bg-sidebar-bg p-3">
        <p className="mb-1 text-caption font-medium text-text-secondary">{t("You")}</p>
        <p className="whitespace-pre-wrap break-words text-small">{turn.prompt}</p>
      </div>
      <div className="flex flex-wrap gap-3 text-caption text-text-secondary">
        <button
          type="button"
          disabled={busy}
          onClick={() => update(turn.saved ? "unsave-action" : "save-action", turn.id)}
          className="inline-flex items-center gap-1 hover:text-accent"
        >
          <BookmarkSimple size={13} weight={turn.saved ? "fill" : "regular"} aria-hidden />
          {turn.saved ? t("Action saved") : t("Save action")}
        </button>
        <button type="button" onClick={() => reuse(turn.prompt)} className="hover:text-accent">
          {t("Reuse")}
        </button>
      </div>
      <div className="min-w-0 break-words text-small">
        <p className="mb-1 text-caption font-medium text-text-secondary">
          {t("Workspace thinker")} ·{" "}
          {turn.status === "proposed" ? t("For your review") : t("TL;DR")}
        </p>
        {turn.status !== "failed" && paragraphs[0] ? <Prose text={paragraphs[0]} /> : null}
        {turn.status !== "failed" && paragraphs.length > 1 ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-caption text-accent">
              {t("Read detail")}
            </summary>
            <div className="mt-2">
              <Prose text={paragraphs.slice(1).join("\n\n")} />
            </div>
          </details>
        ) : null}
        {turn.status === "thinking" ? (
          <p role="status">{t("Thinking… You can return to your page.")}</p>
        ) : null}
        {turn.workerName ? (
          <p className="mt-2 text-caption text-text-secondary">
            {turn.workerName} ·{" "}
            {turn.status === "working" ? (row.workerStatus ?? t("Working")) : t("Reported")}
          </p>
        ) : null}
        {turn.workerResult ? (
          <details className="mt-2">
            <summary className="cursor-pointer text-caption text-accent">
              {t("Worker report")}
            </summary>
            <Prose text={turn.workerResult} />
          </details>
        ) : null}
        {turn.pileId ? (
          <Link
            to="/bench"
            search={{ pile: turn.pileId }}
            className="mt-2 inline-block text-accent underline"
          >
            {t("Open pile on Bench")}
          </Link>
        ) : null}
      </div>
      {turn.status === "proposed" && turn.plan ? (
        <div className="rounded-md border border-divider p-3">
          <p className="mb-3 whitespace-pre-wrap text-small">{turn.plan.brief}</p>
          {turn.plan.kind === "delegate" ? (
            <p className="mb-3 text-caption text-text-secondary">
              {t("Delegate to {{name}}", { name: turn.workerName ?? turn.plan.workerId })}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={inactive || proposal.isPending || !!receiptError}
              onClick={() =>
                turn.plan?.kind === "action"
                  ? proposal.mutate()
                  : void action("execute", { turnId: turn.id }).catch(() => {})
              }
            >
              {turn.plan.kind === "gather"
                ? t("Gather on Bench")
                : turn.plan.kind === "delegate"
                  ? t("Approve delegation")
                  : t("Review action")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={inactive}
              onClick={() => update("dismiss", turn.id)}
            >
              {t("Dismiss")}
            </Button>
          </div>
          {proposal.error ? (
            <p role="alert" className="mt-2 text-small text-danger">
              {proposal.error.message}
            </p>
          ) : null}
          {proposal.data && !inactive ? (
            <CommandProposalCard
              proposal={proposal.data}
              onDiscard={() => proposal.reset()}
              onDone={() => proposal.reset()}
              onApplied={async (receiptId) => {
                if (!receiptId) {
                  setReceiptError(
                    t(
                      "The action ran, but its conversation receipt could not be saved. Check TL;DR before retrying.",
                    ),
                  );
                  return;
                }
                try {
                  await action("record-action", { turnId: turn.id, receiptId });
                } catch {
                  setReceiptError(
                    t(
                      "The action ran, but its conversation receipt could not be saved. Check TL;DR before retrying.",
                    ),
                  );
                }
              }}
            />
          ) : null}
          {receiptError ? (
            <p role="alert" className="text-small text-danger">
              {receiptError}
            </p>
          ) : null}
        </div>
      ) : null}
      {turn.status === "working" && turn.dispatchId ? (
        <Button
          size="sm"
          variant="neutral"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate()}
        >
          {t("Stop worker")}
        </Button>
      ) : null}
      {cancel.error ? (
        <p role="alert" className="text-small text-danger">
          {cancel.error.message}
        </p>
      ) : null}
      {turn.status === "failed" ? (
        <ActionRequired
          error={turn.answer ?? t("The thinker could not finish.")}
          onRetry={!turn.dispatchId ? retry : undefined}
          onRecheck={!turn.dispatchId ? retry : undefined}
          busy={inactive}
        />
      ) : null}
    </article>
  );
}

function auditLabel(action: string, t: (key: string) => string) {
  const labels: Record<string, string> = {
    prompt: t("Prompt saved"),
    answered: t("Thinker responded"),
    keep: t("Conversation kept"),
    archive: t("Moved to archive"),
    restore: t("Conversation restored"),
    "save-action": t("Action saved"),
    "unsave-action": t("Action removed from saved"),
    dismiss: t("Proposal dismissed"),
    delegated: t("Delegated to worker"),
    executed: t("Action executed"),
    "worker-result": t("Worker result reviewed"),
    "action-result": t("Action receipt recorded"),
  };
  return labels[action] ?? action;
}
